import * as Sentry from '@sentry/react'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { AuthenticateParams, ContentType } from 'configs/constants/enums'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import set from 'lodash/set'
import qs from 'qs'
import { toast } from 'react-toastify'
import { decrypt, encrypt } from 'utils/aes'
import envConfig from '../configs'

// Network error types that should trigger retry logic
const NETWORK_ERROR_CODES = [
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_NETWORK_IO_SUSPENDED',
  'ECONNREFUSED',
  'ENOTFOUND',
  'ECONNRESET',
  'ETIMEDOUT'
]

// Check if error is a network-related error
function isNetworkError(error: AxiosError): boolean {
  return (
    !error.response && // No response received
    (error.code && NETWORK_ERROR_CODES.includes(error.code)) ||
    error.message?.toLowerCase().includes('network error') ||
    error.message?.toLowerCase().includes('connection') ||
    error.message?.toLowerCase().includes('timeout')
  )
}

// Retry function with exponential backoff
async function retryRequest(config: InternalAxiosRequestConfig, retryCount = 0): Promise<any> {
  const maxRetries = 2
  const baseDelay = 1000 // 1 second

  if (retryCount >= maxRetries) {
    throw new Error('Max retries exceeded')
  }

  // Exponential backoff: 1s, 2s, 4s
  const delay = baseDelay * Math.pow(2, retryCount)
  
  await new Promise(resolve => setTimeout(resolve, delay))
  
  try {
    return await axiosInstance(config)
  } catch (error) {
    if (isNetworkError(error as AxiosError)) {
      return retryRequest(config, retryCount + 1)
    }
    throw error
  }
}

const axiosInstance = axios.create({
  baseURL: envConfig.apiBaseUrl,
  headers: {
    Accept: 'application/json;charset=UTF-8',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  },
  paramsSerializer: { serialize: params => qs.stringify(params) }
})

axiosInstance.interceptors.request.use(
  function (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    if (envConfig.aes_secret_key && config?.headers?.['Content-Type'] === ContentType.JSON) {
      set(config, 'data', encrypt(config.data))
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  function (response) {
    if (envConfig.aes_secret_key) {
      set(response, 'data', decrypt(response.data))
    }
    return response
  },
  async function (error: AxiosError) {
    // Handle network errors with retry logic
    if (isNetworkError(error) && error.config) {
      try {
        return await retryRequest(error.config)
      } catch (retryError) {
        // If retries failed, continue with original error handling
        console.warn('Network error: All retries failed', retryError)
      }
    }
    
    return Promise.reject(error)
  }
)

export const api = axiosInstance

function handleUnauthorized() {
  alert('Unauthorized! Please try again or contact support.')
  const rootURL = window?.location?.origin ?? `${window.location.protocol}//${import.meta.env.REACT_APP_BASE_PATH}/`
  // window.location.replace(rootURL)
}

const handleUnauthorizedWithDebounce = debounce(handleUnauthorized, 500)

api.interceptors.request.use(function (config: any) {
  const token = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN)
  if (token && !config.headers.Authorization) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` }
  }
  return config
})

api.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (
      error?.message?.includes('401') ||
      (error?.config?.url === '/users/me' && error?.config?.url !== '/auth/login')
    ) {
      handleUnauthorizedWithDebounce()
    }
    return Promise.reject(error)
  }
)

export * from './releaseNote'
export * from './jira'

export enum EErrorType {
  BAD_REQUEST = '400',
  UNAUTHORIZED = '401',
  FORBIDDEN = '403',
  NOT_FOUND = '404',
  UNPROCESSABLE_ENTITY = '422',
  TOO_MANY_REQUESTS = '429',
  INTERNAL_SERVER_ERROR = '500'
}

const errorTypes: EErrorType[] = [
  EErrorType.BAD_REQUEST,
  EErrorType.UNAUTHORIZED,
  EErrorType.FORBIDDEN,
  EErrorType.NOT_FOUND,
  EErrorType.UNPROCESSABLE_ENTITY,
  EErrorType.TOO_MANY_REQUESTS,
  EErrorType.INTERNAL_SERVER_ERROR
]

export function handleError(
  error: Error,
  filePath: string,
  functionName: string,
  keepThrowing: boolean = true,
  showToast: boolean = false,
  group: string = ''
) {
  const errorPath = `Error: ${filePath} -> ${functionName} -> error: ${error}`
  console.error(errorPath, JSON.stringify(error))
  
  // Check if this is a network error
  const axiosError = error as AxiosError
  const isNetworkIssue = isNetworkError(axiosError)
  
  if (!errorTypes.some(type => errorPath.includes(type))) {
    if (import.meta.env.REACT_APP_FLAGS_CTX_APP_NAME !== 'development') {
      if (!isEmpty(group)) {
        Sentry.setTag('group', group)
      }
      
      // Add network error context to Sentry
      if (isNetworkIssue) {
        Sentry.setTag('error_type', 'network_error')
        Sentry.setTag('network_code', axiosError.code || 'unknown')
      }
      
      Sentry.captureException({
        errorPath,
        error: JSON.stringify(error)
      })
    }
  }

  if (showToast) {
    // Provide better error messages for network issues
    if (isNetworkIssue) {
      toast.error('Connection issue detected. Please check your internet connection and try again.')
    } else {
      toast.error(error.message)
    }
  }
  
  if (keepThrowing) {
    throw error
  }
}
